/*
  Product Sticky ATC
  - Mirrors the add-button state
  - only shows when intersectionObserver says main add button is above the viewport
  - mirrors sold out state of product options as shown the theme
  - mirrors swatches as displayed in the product options selectors
  - does not display if window width on page load is 768px or more
  - does not contain it's own form, only acts as a proxy for main product form
*/

/**
 * @fileoverview Sticky Add-to-Cart widget for mobile product pages
 *
 * Provides a sticky, mobile-optimized add-to-cart interface that appears when
 * the main product form's add button scrolls above the viewport. Acts as a proxy
 * to the primary product form without containing its own form logic.
 *
 * @example
 * // Include in product template:
 * // {% render 'product-sticky-atc', current_variant: current_variant %}
 * // The component auto-initializes as a custom element <product-sticky-atc>
 */

/**
 * ProductStickyAtc - Custom HTML element for mobile sticky add-to-cart widget
 *
 * @class ProductStickyAtc
 * @extends HTMLElement
 *
 * @description
 * Mirrors the state and functionality of the main product form's add-to-cart button
 * and option selectors. Displays as a sticky widget when the main button scrolls
 * out of view on mobile devices (viewport width < 767px).
 *
 * Key behaviors:
 * - Only loads on mobile (window width < 767px at initialization)
 * - Shows/hides based on IntersectionObserver tracking the main add button
 * - Mirrors option selector state, swatches, and sold-out status
 * - Syncs variant changes between sticky and main product form
 * - Clones button and price elements to match main form styling
 * - Dispatches events to main form (no independent form submission)
 *
 * @property {HTMLElement} form - Reference to the product form
 * @property {HTMLElement} stickyAtc - The sticky widget DOM element
 * @property {HTMLElement} mainAddButton - The main form's add-to-cart button
 * @property {NodeList} productFormInputs - All option inputs in the main form
 * @property {NodeList} selectedOptions - Displayed option values in sticky widget
 * @property {Function} isMobile - Bound check for mobile breakpoint
 *
 * @fires variantChange - Listens for variant change events from product form
 * @listens variantChange - Updates sticky widget when variant selection changes
 *
 * @example
 * // Auto-initialized when template is rendered:
 * <product-sticky-atc>
 *   <template><!-- sticky widget HTML --></template>
 *   <script type="json">{ "product": {...}, "current_variant": {...} }</script>
 * </product-sticky-atc>
 */

const selectors = {
  productForm: {
    buttonsWrapper: '.add-to-cart-wrapper',
    priceWrapper: '.product__price',
  },
  stickyAtc: {
    buttonsWrapper: '.product-sticky-atc_buttons',
    currentSelectionWrapper: '.product-sticky-atc_current-selection',
    priceWrapper: '.product-sticky-atc_product-price',
    selectorsWrapper: '.product-sticky-atc_selectors',
    variantOption: '.product-sticky-atc_current-variant-option',
  }
}
class ProductstickyAtc extends HTMLElement {
  constructor() {
    super()
    // do not load if not mobile breakpoint
    this.isMobile = () => window.innerWidth < 767
    if(!this.isMobile()) return

    this.form = this.closest('form')
    this.formId = this.closest('form') ? this.closest('form').getAttribute('id') : false
    if(!this.formId) return

    // append the element from the template to the main element
    this.template = this.querySelector('template')
    this.stickyAtc = document.body.appendChild(this.template.content.cloneNode(true).firstElementChild)
    this.buttonsWrapper = this.stickyAtc.querySelector(selectors.stickyAtc.buttonsWrapper)
    this.priceWrapper = this.stickyAtc.querySelector(selectors.stickyAtc.priceWrapper)
    this.selectedOptions = this.stickyAtc.querySelectorAll(selectors.stickyAtc.variantOption)

    // attach main product form add button
    this.mainAddButton = this.form.querySelector('button[name="add"]')

    // create references to main inputs from dropdown selectors
    this.productFormInputs = this.form.querySelectorAll('input[name^="option-"]')
    this.productFormInputs.forEach(input => {
      const button = input.parentElement
      const option = {
        index: input.name.replace('option-', ''),
        value: input.value
      }
      button.stickySibling = this.stickyAtc.querySelector(`.product-sticky-atc_product-option-selector[data-option-index="${option.index}"] [data-value="${option.value}"]`)
      button.stickySibling.mainSibling = button
      // check for swatch
      const label = button.querySelector('label')
      if(label.style.backgroundImage || label.style.background) {
        button.stickySibling.style.setProperty('--product-atc-swatch-image', label.style.backgroundImage)
        button.stickySibling.style.setProperty('--product-atc-swatch-color', label.style.background)
        button.stickySibling.classList.add('has-swatch')
      }
    })
  }

  connectedCallback() {
    if(!this.isMobile()) return
    this.addEventListeners()
    this.copyElements()
    this.setupStickyObserver()
  }

  disconnectedCallback() {
    this.stickyAtc.remove()
    this.removeEventListeners()
    this.stickyObserver.disconnect()
  }

  addEventListeners() {
    this.handleVariantChange = this.variantChange.bind(this)
    window.addEventListener('variantChange', this.handleVariantChange)
    
    this.handleClickTargets = this.clickTargets.bind(this)
    this.stickyAtc.addEventListener('click', this.handleClickTargets)

    // watch for sold out changes on product form elements/option selectors
    const productFormButtons = Array.from(this.productFormInputs).map(input => input.parentElement)
    this.handleMutation = this.updateSoldOutState.bind(this)
    const observer = new MutationObserver(this.handleMutation)
    productFormButtons.forEach(button => {
      // Start observing the target element with the required options
      observer.observe(button, {
        attributes: true, // Watch for attribute changes
        attributeFilter: ['class'] // Only observe the 'class' attribute
      })
    })
  }

  copyElements() {
    // copy a2c buttons and price elements into sticky atc widget so they match the primary display

    const productHandle = this.getAttribute('data-product-handle') || ''
    const stickyIdMap = {
      'add-to-cart': `add-to-cart-sticky-${productHandle}`,
      'buy-now': `buy-now-sticky-${productHandle}`
    }

    const buttonsWrapper = this.form.querySelector(selectors.productForm.buttonsWrapper)
    if(buttonsWrapper) {
      const clonedButtons = buttonsWrapper.cloneNode(true)
      clonedButtons.querySelectorAll('[data-heatmap-id]').forEach(button => {
        const id = button.getAttribute('data-heatmap-id') || ''
        const prefix = Object.keys(stickyIdMap).find(key => id.startsWith(`${key}-`))
        if(prefix) {
          button.setAttribute('data-heatmap-id', stickyIdMap[prefix])
        }
      })
      this.buttonsWrapper.replaceChildren(clonedButtons)
    }

    const priceWrapper = this.form.querySelector(selectors.productForm.priceWrapper)
    if(priceWrapper) {
      this.priceWrapper.replaceChildren(priceWrapper.cloneNode(true))
    }
  }

  variantChange(e) {
    const variant = e?.detail?.variant

    // update labels of current selected variant on variant change
    this.selectedOptions.forEach(option => {
      const index = parseInt(option.getAttribute('data-option-index'))
      const input = Array.from(this.productFormInputs).filter(input => input.getAttribute('name') == `option-${index}` && input.checked)[0]
      option.innerText = input.value
      // check for swatch
      const label = input.nextElementSibling
      if(input && label && (label.style.backgroundImage || label.style.background)) {
        option.style.setProperty('--product-atc-swatch-image', label.style.backgroundImage)
        option.style.setProperty('--product-atc-swatch-color', label.style.background)
        option.classList.add('has-swatch')
      } else {
        option.style.setProperty('--product-atc-swatch-image', '')
        option.style.setProperty('--product-atc-swatch-color', '')
        option.classList.remove('has-swatch')
      }
    })
    // copy all a2c buttons and prices from form to sticky area
    this.copyElements()

    this.updateSelectors(variant)
  }

  clickTargets(e) {
    // determine click behaviors within sticky atc widget

    // if a2c button is clicked, click product form submit button
    const clickAddBtn = e.target.nodeName == 'BUTTON' && e.target.getAttribute('type') == 'submit' && e.target.getAttribute('name') == 'add'
    if(clickAddBtn) this.mainAddButton.click()

    // if option selector button is clicked, toggle open class and dropdown, close other dropdowns
    const clickSelectedOptionButton = e.target.nodeName == 'BUTTON' && e.target.hasAttribute('data-selected-option')
    if(clickSelectedOptionButton) this.toggleOptionSelector(e.target)

    // if product option is clicked, change selection in product form
    const clickProductOption = e.target.nodeName == 'LI' && e.target.hasAttribute('data-product-option')
    if(clickProductOption) this.triggerOptionChange(e.target)
  }

  toggleOptionSelector(option) {
    // toggle open or closed an option selector after it's selected value is clicked
    if(option.classList.contains('open')){
      this.closeAllOpenSelectors()
      return
    }
    this.closeAllOpenSelectors()
    const optionIndex = option.getAttribute('data-option-index')
    option.classList.toggle('open')
    const optionSelector = this.stickyAtc.querySelector(`.product-sticky-atc_product-option-selector[data-option-index="${optionIndex}"]`)
    optionSelector.classList.toggle('open')
  }

  closeAllOpenSelectors() {
    // collapse all option selectors
    this.stickyAtc.querySelectorAll('.product-sticky-atc_option-button.open').forEach(selector => selector.classList.remove('open'))
    this.stickyAtc.querySelectorAll('.product-sticky-atc_product-option-selector.open').forEach(selector => selector.classList.remove('open'))
  }

  removeEventListeners() {
    window.removeEventListener('variantChange', this.handleVariantChange)
    this.stickyAtc.removeEventListener('click', this.handleClickTargets)
  }

  triggerOptionChange(option) {
    const optionIndex = option.getAttribute('data-option-index')
    const optionValue = option.getAttribute('data-value')

    const formInput = this.form.querySelector(
      `input[name="option-${optionIndex}"][value="${optionValue}"],
      select[name="option-${optionIndex}"]`
    )

    if (!formInput) return;

    if (formInput.nodeName === 'SELECT') {
      formInput.value = optionValue
    } else {
      formInput.checked = true
    }

    // dispatch events to make sure form functions correctly
    formInput.dispatchEvent(new Event('input', { bubbles: true }))
    formInput.dispatchEvent(new Event('change', { bubbles: true }))
  }

  updateSoldOutState(mutationsList, observer) {
    // Iterate over all mutations that occurred and determine whether to add soldout class to dropdown item
    for (const mutation of mutationsList) {
      if (mutation.attributeName === 'class') {
        const stickyTarget = mutation.target.stickySibling
        mutation.target.classList.contains('soldout') ? stickyTarget.classList.add('soldout') : stickyTarget.classList.remove('soldout')
      }
    }
  }

  updateSelectors(variant) {
    // update displayed selected value when variant changes
    const selectors = this.stickyAtc.querySelectorAll('.product-sticky-atc_product-option-selector')
    selectors.forEach(selector => {
      selector.querySelectorAll('li').forEach(option => {
        const input = option.mainSibling.querySelector('input')
        option.setAttribute('aria-selected', input.checked)
      })
    })
  }

  setupStickyObserver() {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the button is NOT intersecting and is above the viewport
        const isAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0
        
        this.stickyAtc.classList.toggle('is-visible', isAboveViewport)
        document.body.classList.toggle('product-sticky-atc-visible', isAboveViewport)
      },
      {
        threshold: 0,       // Trigger as soon as any part leaves/enters
        rootMargin: '0px',  // No margin offset — fires exactly at viewport edge
      }
    );
  
    observer.observe(this.mainAddButton)
  }
}
customElements.define('product-sticky-atc', ProductstickyAtc)
